<template>
  <div class="proxy-tester-page dark-theme">
    <div class="proxy-container">
      <!-- 页面标题 -->
      <div class="page-header">
        <div class="header-icon">
          <a-icon type="api" />
        </div>
        <div class="header-text">
          <h2 class="page-title">{{ translate('proxy_test') }}</h2>
          <p class="page-description">{{ translate('proxy_test_description') }}</p>
        </div>
      </div>
      
      <!-- 主内容区 -->
      <div class="main-content">
        <!-- 顶部配置区域 -->
        <div class="top-section">
          <!-- 左侧说明卡片 -->
          <div class="section-card instructions-card">
            <div class="card-header">
              <a-icon type="info-circle" class="card-icon" />
              <h3 class="card-title">{{ translate('instructions') }}</h3>
            </div>
            <div class="instructions-content">
              <ol>
                <li>{{ translate('proxy_instruction_1') }}</li>
                <li>{{ translate('proxy_instruction_2') }}</li>
                <li>{{ translate('proxy_instruction_3') }}</li>
              </ol>
              <div class="format-info">
                <p class="format-label">{{ translate('supported_format') }}：</p>
                <p class="format-example">192.168.0.1:8000:{{ translate('proxy_account') }}:{{ translate('proxy_password') }}</p>
              </div>
            </div>
          </div>
          
          <!-- 右侧表单卡片 -->
          <div class="section-card form-card">
            <div class="card-header">
              <a-icon type="setting" class="card-icon" />
              <h3 class="card-title">{{ translate('proxy_config') }}</h3>
            </div>
            <a-form :model="form" ref="form" layout="vertical" class="form-container">
              <div class="form-row">
                <div class="form-group">
                  <a-form-item :label="translate('ip_query_channel')">
                    <a-select v-model="form.ipQueryChannel" :placeholder="translate('please_select')" class="form-control">
                      <a-select-option
                        v-for="item in ipQueryChannels"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </div>
                <div class="form-group">
                  <a-form-item :label="translate('proxy_type')">
                    <a-select v-model="form.proxyType" :placeholder="translate('please_select')" class="form-control">
                      <a-select-option
                        v-for="item in proxyTypes"
                        :key="item.value"
                        :value="item.value"
                      >
                        {{ item.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </div>
              </div>
              <div class="form-row full-width">
                <div class="form-group full-width">
                  <a-form-item :label="translate('proxy_info')">
                    <a-textarea
                      v-model="form.proxyInfo"
                      @change="onChange"
                      :placeholder="translate('please_input_content')"
                      :rows="6"
                      class="form-control"
                    />
                  </a-form-item>
                </div>
              </div>
              <div class="form-actions">
                <a-button type="primary" class="action-btn" @click="checkProxy">
                  {{ translate('check_proxy') }}
                </a-button>
              </div>
            </a-form>
          </div>
        </div>
        
        <!-- 底部表格区域 -->
        <div class="bottom-section">
          <div class="section-card table-card">
            <div class="card-header">
              <a-icon type="table" class="card-icon" />
              <h3 class="card-title">{{ translate('proxy_result') }}</h3>
            </div>
            <a-table
              :columns="columns"
              :data-source="proxyList"
              :row-key="(record, index) => `${record.ip}:${record.port}:${index}`"
              :pagination="false"
              class="dark-table"
            >
              <template slot="status" slot-scope="cell, record">
                <template v-if="record">
                  <div v-if="record.loading">
                    <a-spin size="small" />
                  </div>
                  <div v-else class="status-indicator">
                    {{ record.status }}
                  </div>
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  inject: ['languageState'],
  data() {
    // 使用默认语言初始化，避免computed-in-data问题
    const langTranslations = {
      en: {
        proxy_test: 'Proxy Test',
        proxy_test_description: 'Please fill in the proxy information and click the Check Proxy button to test proxy availability',
        instructions: 'Instructions',
        proxy_instruction_1: 'If proxy type is not filled in, it will be added according to the set proxy type',
        proxy_instruction_2: 'Only HTTP and Socks5 proxy types are supported',
        proxy_instruction_3: 'One proxy per line, up to 20 at a time',
        supported_format: 'Supported format',
        proxy_account: 'proxy_account',
        proxy_password: 'proxy_password',
        ip_query_channel: 'IP Query Channel',
        proxy_type: 'Proxy Type',
        proxy_info: 'Proxy Information',
        check_proxy: 'Check Proxy',
        please_select: 'Please select',
        please_input_content: 'Please input content',
        proxy_type_col: 'Proxy Type',
        proxy_host_col: 'Proxy Host',
        proxy_port_col: 'Proxy Port',
        proxy_account_col: 'Proxy Account',
        proxy_password_col: 'Proxy Password',
        ip_channel_col: 'IP Channel',
        exit_ip_col: 'Exit IP',
        status_col: 'Status',
        unchecked: 'Unchecked',
        checking: 'Checking',
        available: 'Available',
        unavailable: 'Unavailable',
        check_failed: 'Check Failed',
        proxy_config: 'Proxy Configuration',
        proxy_result: 'Proxy Test Results'
      },
      zh: {
        proxy_test: '代理测试',
        proxy_test_description: '请填写代理信息，点击检查代理按钮测试代理可用性',
        instructions: '说明',
        proxy_instruction_1: '不填写代理类型时，将按照设置的代理类型进行添加',
        proxy_instruction_2: '代理类型仅支持HTTP、Socks5',
        proxy_instruction_3: '一行一个代理，一次最多添加20个',
        supported_format: '支持以下填写格式',
        proxy_account: '代理账号',
        proxy_password: '代理密码',
        ip_query_channel: 'IP查询渠道',
        proxy_type: '代理类型',
        proxy_info: '代理信息',
        check_proxy: '检查代理',
        please_select: '请选择',
        please_input_content: '请输入内容',
        proxy_type_col: '代理类型',
        proxy_host_col: '代理主机',
        proxy_port_col: '代理端口',
        proxy_account_col: '代理账号',
        proxy_password_col: '代理密码',
        ip_channel_col: 'IP查询渠道',
        exit_ip_col: '出口IP',
        status_col: '状态',
        unchecked: '未检查',
        checking: '正在检查',
        available: '可用',
        unavailable: '不可用',
        check_failed: '检查失败',
        proxy_config: '代理配置',
        proxy_result: '代理测试结果'
      }
    };
    return {
      form: {
        ipQueryChannel: 'IPinfo',
        proxyType: 'Socks5',
        proxyInfo: ''
      },
      langTranslations,
      proxyList: [],
      ipQueryChannels: [
        { value: 'IPinfo', label: 'IPinfo' }
      ],
      proxyTypes: [
        { value: 'Socks5', label: 'Socks5' },
        { value: 'HTTP', label: 'HTTP' }
      ],
      loading: false
    };
  },
  computed: {
    currentLanguage() {
      return this.languageState?.languageCode || 'en';
    },
    columns() {
      return [
        { title: this.translate('proxy_type_col'), dataIndex: 'proxytype', key: 'proxytype', width: 100 },
        { title: this.translate('proxy_host_col'), dataIndex: 'ip', key: 'ip', width: 100 },
        { title: this.translate('proxy_port_col'), dataIndex: 'port', key: 'port', width: 100 },
        { title: this.translate('proxy_account_col'), dataIndex: 'account', key: 'account', width: 100 },
        { title: this.translate('proxy_password_col'), dataIndex: 'password', key: 'password', width: 100 },
        { title: this.translate('ip_channel_col'), dataIndex: 'ipchanel', key: 'ipchanel', width: 100 },
        { title: this.translate('exit_ip_col'), dataIndex: 'ipout', key: 'ipout', width: 100 },
        { title: this.translate('status_col'), dataIndex: 'status', key: 'status', width: 100, scopedSlots: { customRender: 'status' } }
      ];
    }
  },
  watch: {
    'form.proxyType': function(newType) {
      this.proxyList.forEach(proxy => {
        this.$set(proxy, 'proxytype', newType);
      });
    }
  },
  methods: {
    translate(key) {
      const lang = this.currentLanguage;
      return this.langTranslations[lang][key] || key;
    },
    parseProxyString(str) {
      const parts = str.split(':');
      const host = parts[0];
      const port = parts[1];
      const account = parts[2] || '';
      const password = parts[3] || '';

      return {
        proxytype: this.form.proxyType,
        ip: host,
        port: port,
        account: account || '-',
        password: password || '-',
        ipchanel: this.form.ipQueryChannel,
        ipout: '-',
        status: this.translate('unchecked'),
        loading: false
      };
    },
    onChange(e) {
      const value = e.target.value;
      const lines = value.split('\n').filter(line => line.trim() !== '');
      this.proxyList = lines.map(this.parseProxyString).slice(0, 20);
    },
    async checkProxy() {
      for (let i = 0; i < this.proxyList.length; i++) {
        const proxy = this.proxyList[i];
        this.$set(proxy, 'loading', true);
        this.$set(proxy, 'status', this.translate('checking'));
        try {
          const res = await this.$request.post("/checkproxy/check", {
            proxyPort: proxy.port,
            proxyHost: proxy.ip,
            username: proxy.account,
            password: proxy.password,
            proxytype: proxy.proxytype
          });
          if (res.data.code === 200) {
            this.$set(proxy, 'status', this.translate('available'));
            const eachData = JSON.parse(res.data.data);
            this.$set(proxy, 'ipout', `${eachData.ip}\n${eachData.country_code}\n${eachData.country_name}`);
          } else {
            this.$set(proxy, 'status', this.translate('unavailable'));
          }
        } catch (error) {
          console.error('检查代理失败', error);
          this.$set(proxy, 'status', this.translate('check_failed'));
        } finally {
          this.$set(proxy, 'loading', false);
        }
      }
    }
  }
};
</script>

<style lang="less" scoped>
// 深色主题全局样式
.dark-theme {
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  color: #ffffff;
  min-height: 100vh;
}

// 主容器样式
.proxy-tester-page {
  padding: 20px;
  min-height: 100vh;
}

.proxy-container {
  max-width: 1600px;
  margin: 0 auto;
}

// 页面标题
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  margin-bottom: 24px;
  background: rgba(30, 41, 59, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(21, 129, 198, 0.2);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.header-icon {
  width: 56px;
  height: 56px;
  background: rgba(21, 129, 198, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .anticon {
    font-size: 28px;
    color: #1581c6;
  }
}

.header-text {
  flex: 1;
}

.page-title {
  font-size: 26px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 6px 0;
  letter-spacing: 0.5px;
}

.page-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.6;
}

// 主内容区
.main-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// 顶部区域
.top-section {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
}

// 卡片样式
.section-card {
  background: rgba(30, 41, 59, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(21, 129, 198, 0.2);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
    transform: translateY(-2px);
  }
}

// 卡片头部
.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(21, 129, 198, 0.2);
}

.card-icon {
  font-size: 20px;
  color: #1581c6;
  background: rgba(21, 129, 198, 0.1);
  padding: 8px;
  border-radius: 8px;
}

.card-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
}

// 说明内容
.instructions-content {
  color: rgba(255, 255, 255, 0.8);
  
  ol {
    margin: 0 0 20px 0;
    padding-left: 24px;
    
    li {
      margin-bottom: 12px;
      line-height: 1.6;
      font-size: 14px;
    }
  }
}

.format-info {
  margin-top: 20px;
  padding: 16px;
  background: rgba(21, 129, 198, 0.1);
  border-radius: 12px;
  border-left: 4px solid #1581c6;
}

.format-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
  margin: 0 0 8px 0;
}

.format-example {
  font-size: 13px;
  color: #1581c6;
  font-family: 'Courier New', monospace;
  background: #000000;
  padding: 8px 12px;
  border-radius: 8px;
  margin: 0;
  overflow-x: auto;
  white-space: nowrap;
  border: 1px solid rgba(21, 129, 198, 0.3);
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #1581c6;
    border-radius: 3px;
    
    &:hover {
      background: #1976d2;
    }
  }
}

// 表单样式
.form-card {
  flex: 1;
}

.form-container {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  
  &.full-width {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
}

.form-group {
  flex: 1;
  
  &.full-width {
    flex: none;
    width: 100%;
  }
}

.form-control {
  width: 100%;
  padding: 0 !important;
  
  :deep(.ant-select-selector),
  :deep(.ant-input),
  :deep(.ant-input-textarea) {
    background: #000000 !important;
    border: 1px solid rgba(21, 129, 198, 0.3) !important;
    border-radius: 10px !important;
    color: #ffffff !important;
    font-size: 15px !important;
    
    &:focus,
    &:hover {
      border-color: #1581c6 !important;
      box-shadow: 0 0 0 2px rgba(21, 129, 198, 0.2) !important;
    }
    
    input,
    textarea {
      color: #ffffff !important;
      font-size: 15px !important;
      background: #000000 !important;
    }
  }
  
  // 确保textarea有明显边框
  :deep(.ant-input-textarea) {
    border: 1px solid rgba(21, 129, 198, 0.5) !important;
    
    &:focus {
      border-color: #1581c6 !important;
    }
  }
  
  :deep(.ant-select-selection-item) {
    color: #ffffff !important;
  }

  :deep(.ant-select-selection) {
    border: none !important;
  }
  
  :deep(.ant-select-dropdown) {
    background: rgba(30, 41, 59, 0.95) !important;
    border: 1px solid rgba(21, 129, 198, 0.3) !important;
    border-radius: 10px !important;
  }
  
  :deep(.ant-select-item-option) {
    color: rgba(255, 255, 255, 0.9) !important;
    
    &.ant-select-item-option-selected {
      background: rgba(21, 129, 198, 0.2) !important;
    }
    
    &:hover {
      background: rgba(21, 129, 198, 0.1) !important;
    }
  }
}

// Form label style
:deep(.ant-form-item-label > label) {
  font-size: 15px;
  font-weight: 600;
  color: #ffffff !important;
  padding: 0 !important;
  margin-right: 0;
}

// Ant Design form item label text
:deep(.ant-form-item-label) {
  color: #ffffff !important;
}

// Ant Design label component
:deep(.ant-form-item-label label) {
  color: #ffffff !important;
}

.form-actions {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.action-btn {
  height: 48px;
  font-size: 15px;
  font-weight: 600;
  padding: 0 32px;
  border-radius: 10px !important;
  line-height: 48px !important;
  background: linear-gradient(135deg, #1581c6 0%, #1581c6 100%) !important;
  border: none !important;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #ffffff !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(21, 129, 198, 0.4) !important;
    background: linear-gradient(135deg, #1581c6 0%, #1581c6 100%) !important;
  }
  
  &:active {
    transform: translateY(0);
  }
}

// 底部表格区域
.bottom-section {
  margin-top: 0;
}

// 深色表格样式
.dark-table {
  width: 100%;
  background: transparent !important;
  
  :deep(.ant-table) {
    background: transparent !important;
    color: #ffffff !important;
  }
  
  :deep(.ant-table-container) {
    background: transparent !important;
  }
  
  :deep(.ant-table-thead > tr > th) {
    background: rgba(51, 65, 85, 0.8) !important;
    border-bottom: 1px solid rgba(21, 129, 198, 0.2) !important;
    color: rgba(255, 255, 255, 0.8) !important;
    font-weight: 700 !important;
    font-size: 14px;
    padding: 16px;
  }
  
  :deep(.ant-table-tbody > tr) {
    background: transparent !important;
    border-bottom: 1px solid rgba(21, 129, 198, 0.1) !important;
  }
  
  :deep(.ant-table-tbody > tr > td) {
    color: rgba(255, 255, 255, 0.7) !important;
    font-size: 14px;
    padding: 16px;
    background: rgba(30, 41, 59, 0.3) !important;
  }
  
  :deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(21, 129, 198, 0.2) !important;
  }
  
  // 表格空状态样式
  :deep(.ant-empty) {
    color: rgba(255, 255, 255, 0.5) !important;
    background: #000000 !important;
    border: 1px solid rgba(21, 129, 198, 0.3) !important;
    border-radius: 8px;
    padding: 40px 20px !important;
    margin: 20px 0 !important;
  }
  
  :deep(.ant-empty-image svg) {
    fill: rgba(255, 255, 255, 0.2) !important;
    stroke: rgba(255, 255, 255, 0.3) !important;
  }
  
  :deep(.ant-empty-description) {
    color: rgba(255, 255, 255, 0.5) !important;
  }
  
  :deep(.ant-table-placeholder) {
    background: transparent !important;
    border: none !important;
  }
}

// 状态指示器
.status-indicator {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &:contains('Available') {
    background: rgba(82, 196, 26, 0.2);
    color: #52c41a;
  }
  
  &:contains('Unavailable') {
    background: rgba(245, 34, 45, 0.2);
    color: #f5222d;
  }
  
  &:contains('Checking') {
    background: rgba(247, 186, 42, 0.2);
    color: #f7ba2a;
  }
  
  &:contains('Unchecked') {
    background: rgba(153, 153, 153, 0.2);
    color: #999;
  }
  
  &:contains('Check Failed') {
    background: rgba(245, 34, 45, 0.2);
    color: #f5222d;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .proxy-tester-page {
    padding: 16px;
  }
  
  .page-header {
    padding: 20px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .header-icon {
    width: 48px;
    height: 48px;
    
    .anticon {
      font-size: 24px;
    }
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .top-section {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    flex-direction: column;
    gap: 16px;
  }
  
  .form-actions {
    justify-content: center;
  }
  
  .action-btn {
    width: 100%;
  }
}
</style>